import React, { useContext, useEffect } from 'react';
import {
  Text,
  Spin,
  Color,
  Flex,
  Select,
  Drawer,
  Pagination,
} from '@bb/elements-web';
import { AuthContext } from 'contexts';
import styled from 'styled-components';
import { useShowingsCalendarContext } from '../ShowingsCalendarContext';
import { useManagedAccessDrawerContext } from './ManagedAccessDrawerContext';
import useManagedAccessByUser from '../hooks/useManagedAccessByUser';
import ManagedAccessRecordRow from './ManagedAccessDrawerRow';

const ManagedAccessDrawer: React.FC = () => {
  const { auth } = useContext(AuthContext);
  const { managedAccessDrawerOpen, setManagedAccessDrawerOpen } =
    useManagedAccessDrawerContext();
  const {
    selectedMenuOption,
    setManagedAccessCount,
    listingId,
    setManagedAccessLoading,
  } = useShowingsCalendarContext();
  const {
    records,
    totalCount,
    page,
    setPage,
    isLoading,
    pageSize,
    listingFilter,
    setListingFilter,
    hasError,
    listingFilterOptions,
  } = useManagedAccessByUser({
    selectedMenuOption,
    authId: auth.id,
    listingId,
  });

  // set loading state and total count to display in the button that opens the drawer on the Showings page
  useEffect(() => {
    if (isLoading) {
      setManagedAccessLoading(true);
    } else {
      setManagedAccessCount(totalCount);
      setManagedAccessLoading(false);
    }
  }, [totalCount, isLoading, setManagedAccessLoading, setManagedAccessCount]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <StyledCenteredState>
          <Spin dataCy="managed-access-drawer-loading" />
        </StyledCenteredState>
      );
    }
    if (hasError) {
      return (
        <StyledCenteredState>
          <Text>Failed to load managed access records.</Text>
          <Text>If the problem persists, contact support.</Text>
        </StyledCenteredState>
      );
    }
    if (records.length === 0) {
      return (
        <StyledCenteredState>
          <Text>No managed access appointments found.</Text>
        </StyledCenteredState>
      );
    }
    return (
      <>
        {records.map(record => {
          const showGrantee = record.granteeId !== auth.id;

          return (
            <ManagedAccessRecordRow
              key={record.id}
              record={record}
              showGrantee={showGrantee}
            />
          );
        })}
      </>
    );
  };

  return (
    <StyledDrawer
      title="Managed access appointments"
      placement="right"
      width={400}
      open={managedAccessDrawerOpen}
      onClose={() => setManagedAccessDrawerOpen(false)}
      destroyOnClose={false}
      drawerStyle={{ backgroundColor: Color.WHITE }}
      closable
      zIndex={1011} // the 'assume by' navbar has z-index of 1010, so this should be above that
      footer={
        totalCount > pageSize && (
          <StyledPaginationContainer>
            <Pagination
              current={page}
              pageSize={pageSize}
              total={totalCount}
              onChange={setPage}
              showSizeChanger={false}
              size="small"
            />
          </StyledPaginationContainer>
        )
      }
    >
      <Flex flex={1} vertical gap={12}>
        {selectedMenuOption === 'Listing' && (
          <StyledSelect
            dataCy="managed-access-drawer-select"
            value={listingFilter || 'viewAll'}
            required
            width={'100%'}
            onChange={val => {
              setListingFilter(val as string);
            }}
            options={[{ label: 'View All', value: 'viewAll' }].concat(
              listingFilterOptions.map(option => ({
                label: option.address,
                value: option.id,
              })),
            )}
          />
        )}
        {renderContent()}
      </Flex>
    </StyledDrawer>
  );
};

const StyledSelect = styled(Select)`
  margin-bottom: 12px;

  .ant5-select-selector {
    border-color: ${Color.BORDER_GREY} !important;
  }
`;

const StyledDrawer = styled(Drawer)`
  .ant5-drawer-header {
    border: none;
    padding-bottom: 0;
  }

  .ant5-drawer-title {
    font-size: 20px;
    font-weight: 500;
  }

  .ant5-drawer-header-title {
    flex-direction: row-reverse;
  }
`;

const StyledCenteredState = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  align-items: center;
  padding: 40px 0;
`;

const StyledPaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0 8px;
`;

export default ManagedAccessDrawer;
